// BBCSplitApprovalPage.jsx
// KEY FIX: No component functions defined inside the render scope.
// All JSX inlined — prevents React unmount/remount on every keystroke.
//
// NEW: spendingInfo prop — shows BlitzPay spending power panel.
//      No fee deducted from the approver's side; the SME absorbs the Blitz fee.
//      Approval is blocked if total_available_funds < invoice total.

import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Avatar, Button, Chip, Divider,
  IconButton, Stack, Paper, CircularProgress, Alert,
  Collapse, TextField, RadioGroup, Radio, FormControlLabel,
  Checkbox, Grid, LinearProgress, Tooltip, useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Bolt as BoltIcon, Close as CloseIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  Business as BusinessIcon, CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon, Person as PersonIcon,
  Warning as WarningIcon, Security as SecurityIcon,
  Gavel as GavelIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, Upload as UploadIcon,
  Check as CheckIcon, AccountBalanceWallet as WalletIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import SignatureCanvas from "react-signature-canvas";
import client from "../../../API";

const Y        = "#FFD600";
const Y_DARK   = "#F5C800";
const Y_DEEP   = "#B8960C";
const Y_INK    = "#1A1200";
const Y_TINT   = alpha("#FFD600", 0.07);
const Y_BORDER = alpha("#FFD600", 0.25);

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n ?? 0);
const termLabel = (days) =>
  days == null ? "Standard" : days === 0 ? "Due on receipt" : `Net ${days}`;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const BBCSplitApprovalPage = ({
  open,
  onClose,
  invoice: invoiceProp,
  onSuccess,
  // spendingInfo: same shape TeamSpendingCard uses from the backend spending endpoint.
  // Pass this in from the parent page so we don't need an extra fetch here.
  spendingInfo = null,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [payments, setPayments]           = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterOpen, setRosterOpen]       = useState(true);
  const [hasExistingNoa, setHasExistingNoa] = useState(false);
  const [noaData, setNoaData]             = useState(null);

  const [action, setAction]       = useState("approve");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const [approveWord, setApproveWord]   = useState("");
  const [reasoning, setReasoning]       = useState("");
  const [apEmail, setApEmail]           = useState("");
  const [poRef, setPoRef]               = useState("");
  const [erp, setErp]                   = useState("");
  const [externalPo, setExternalPo]     = useState("");
  const [authorityType, setAuthorityType] = useState("leader");
  const [agreeTerms, setAgreeTerms]     = useState(false);
  const [agreeAuthority, setAgreeAuthority] = useState(false);
  const [proofType, setProofType]       = useState("upload");
  const [file, setFile]                 = useState(null);
  const [filePreview, setFilePreview]   = useState(null);
  const [apSectionOpen, setApSectionOpen] = useState(true);
  const [justifyOpen, setJustifyOpen]   = useState(false);

  const sigCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open || !invoiceProp) return;
    setAction("approve"); setApproveWord(""); setReasoning("");
    setApEmail(""); setPoRef(invoiceProp.po_number || ""); setErp(""); setExternalPo("");
    setAuthorityType("leader"); setAgreeTerms(false); setAgreeAuthority(false);
    setProofType("upload"); setFile(null); setFilePreview(null);
    setPayments([]); setHasExistingNoa(false); setNoaData(null);
    setApSectionOpen(true); setJustifyOpen(false); setConfirmed(null); setError(null);
    sigCanvasRef.current?.clear();
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!invoiceProp.id) return;
    let cancelled = false;
    setRosterLoading(true);
    (async () => {
      try {
        const data = await client.invoices.fetch(invoiceProp.id);
        if (cancelled) return;
        const roster = data?.creator_payments?.length ? data.creator_payments : data?.payouts?.length ? data.payouts : [];
        setPayments(roster);
        const po = data?.invoice?.po_number || data?.po_number;
        if (po) setPoRef(po);
        if (data?.ap_info?.ap_email) setApEmail(data.ap_info.ap_email);
        if (data?.noa_data) {
          setNoaData(data.noa_data);
          setHasExistingNoa(true);
          if (data.noa_data.noa_sent_to) setApEmail(data.noa_data.noa_sent_to);
          setApSectionOpen(false);
        }
        if (data?.ap_info?.ap_info_verified) setApSectionOpen(false);
      } catch { /* silent */ }
      finally { if (!cancelled) setRosterLoading(false); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceProp?.id]);

  if (!invoiceProp) return null;

  // ── Derived ──────────────────────────────────────────────────────────── //
  const inv           = invoiceProp;
  const total         = parseFloat(inv.amount_due || inv.amount || 0);
  const senderName    = inv.client_name || inv.name || "Agency";
  const poNumber      = inv.po_number || "—";
  const paymentTerm   = inv.payment_term ?? null;
  const invoiceStatus = (inv.status || inv.invoice_status || "").toLowerCase();
  const alreadyProcessed = ["approved", "paid", "funded", "cancelled"].includes(invoiceStatus);

  const hasZeroAmount = payments.some((p) => parseFloat(p.amount || 0) === 0);
  const showRoster    = payments.length > 0 && !hasZeroAmount;
  const creatorCount  = payments.length;
  const preCount      = payments.filter((p) => p.preapproved || p.blitzpay_preapproved || p.blitzpay).length;

  // ── Spending power derived values ─────────────────────────────────────── //
  // No fee for the approver — the SME's Blitz fee is baked into the
  // Split before it ever reaches the enterprise.
  const spAvailable = spendingInfo
    ? parseFloat(
        spendingInfo.total_available_funds ||
        spendingInfo.team_available_spending ||
        spendingInfo.company_balance ||
        0
      )
    : null;
  const spAllocated = spendingInfo
    ? parseFloat(
        spendingInfo.team_allocated_spending ||
        spendingInfo.company_spending_limit ||
        0
      )
    : null;
  const spUsed = spendingInfo
    ? parseFloat(spendingInfo.team_used_spending || 0)
    : null;

  const hasInsufficientFunds = spAvailable !== null && spAvailable < total;
  const utilizationAfter = spAllocated && spAllocated > 0
    ? Math.min(((spUsed + total) / spAllocated) * 100, 100)
    : null;

  const getUtilColor = (pct) => {
    if (pct >= 90) return theme.palette.error.main;
    if (pct >= 75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const isApproveValid =
    action !== "approve" || (
      approveWord === "Approve" &&
      emailRegex.test(apEmail.trim()) &&
      !!poRef.trim() &&
      agreeTerms &&
      agreeAuthority &&
      !hasInsufficientFunds
    );

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert("Max 5MB"); return; }
    if (!f.type.startsWith("image/") && f.type !== "application/pdf") { alert("Image or PDF only"); return; }
    setFile(f);
    setFilePreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };

  const buildProof = async () => {
    const proof = {
      reasoning: reasoning || undefined,
      date_confirmed: new Date().toISOString(),
      approval_method: "digital_approval_system",
      approver_confirmation: approveWord,
      ap_email: apEmail.trim(),
      po_ref: poRef.trim(),
      erp_system: erp.trim() || undefined,
      external_po_number: externalPo.trim() || undefined,
      authority_type: authorityType,
      invoice_amount: total,
      invoice_type: "bbc",
      has_existing_noa: hasExistingNoa,
      // Fee note: no fee charged to approver — SME bears the Blitz discount rate.
      fee_charged_to_approver: false,
    };
    if (proofType === "upload" && file) {
      await new Promise((res, rej) => {
        const r = new FileReader();
        r.readAsDataURL(file);
        r.onload  = () => { proof.screenshot_data = r.result; proof.screenshot_filename = file.name; proof.proof_type = "document_upload"; res(); };
        r.onerror = () => rej(new Error("Read failed"));
      });
    }
    if (proofType === "signature" && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      proof.signature_data = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      proof.proof_type     = "digital_signature";
    }
    return proof;
  };

  const handleSubmit = async () => {
    if (!isApproveValid) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { invoiceId: inv.id, action };
      if (action === "approve") payload.approval_proof = await buildProof();
      await client.invoices.unifiedAction(payload);
      setConfirmed(action === "approve" ? "approved" : "declined");
      setTimeout(() => {
        onSuccess?.(action === "approve" ? "Split approved — contractors will be notified." : "Split declined.");
        onClose?.();
      }, 1400);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setApproveWord(""); setReasoning(""); setFile(null); setFilePreview(null);
    onClose?.();
  };

  // ── Success state ─────────────────────────────────────────────────────── //
  if (confirmed) {
    return (
      <Box sx={{ position: "fixed", inset: 0, bgcolor: "background.default", zIndex: theme.zIndex.modal, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
        {confirmed === "approved" ? (
          <>
            <Avatar sx={{ width: 72, height: 72, bgcolor: Y, color: Y_INK }}><CheckCircleIcon sx={{ fontSize: 40 }} /></Avatar>
            <Typography variant="h5" fontWeight={800}>Split approved</Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 360 }}>
              Contractors will be notified.{preCount > 0 && <> {preCount} ⚡ pre-accepted payout{preCount !== 1 ? "s" : ""} convert instantly.</>}
            </Typography>
          </>
        ) : (
          <>
            <Avatar sx={{ width: 72, height: 72, bgcolor: "error.light", color: "error.dark" }}><CancelIcon sx={{ fontSize: 40 }} /></Avatar>
            <Typography variant="h5" fontWeight={800}>Invoice declined</Typography>
            <Typography variant="body1" color="text.secondary">The sender has been notified.</Typography>
          </>
        )}
        <CircularProgress size={20} sx={{ mt: 1, color: Y_DEEP }} />
        <Typography variant="caption" color="text.secondary">Closing…</Typography>
      </Box>
    );
  }

  // ── Spending power panel JSX (inlined) ───────────────────────────────── //
  const spendingPanelJsx = spendingInfo ? (
    <Paper
      variant="outlined"
      sx={{
        p: 2, mb: 3, borderRadius: 2,
        borderColor: hasInsufficientFunds ? "error.main" : alpha(theme.palette.info.main, 0.3),
        bgcolor: hasInsufficientFunds
          ? alpha(theme.palette.error.main, 0.04)
          : alpha(theme.palette.info.main, 0.04),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WalletIcon sx={{ fontSize: 18, color: hasInsufficientFunds ? "error.main" : "info.main" }} />
          <Typography variant="subtitle2" fontWeight={700} color={hasInsufficientFunds ? "error.main" : "text.primary"}>
            BlitzPay Spending Power
          </Typography>
        </Box>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" display="block" fontWeight={700} mb={0.5}>No fee charged to you</Typography>
              <Typography variant="caption" display="block" sx={{ fontSize: "0.7rem" }}>
                The Blitz discount rate is absorbed by the invoicing party (SME).
                You pay exactly {fmt(total)} — no markup.
              </Typography>
            </Box>
          }
          arrow
        >
          <InfoIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>Available</Typography>
        <Typography
          variant="h6"
          fontWeight={800}
          color={hasInsufficientFunds ? "error.main" : "info.main"}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {fmt(spAvailable)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" color="text.secondary">This invoice</Typography>
        <Typography variant="caption" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmt(total)}
        </Typography>
      </Box>

      {utilizationAfter !== null && (
        <>
          <LinearProgress
            variant="determinate"
            value={utilizationAfter}
            sx={{
              height: 6, borderRadius: 3, mb: 0.75,
              bgcolor: alpha(theme.palette.grey[300], 0.4),
              "& .MuiLinearProgress-bar": {
                bgcolor: getUtilColor(utilizationAfter),
                borderRadius: 3,
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {fmt(spUsed)} used
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: hasInsufficientFunds ? "error.main" : "success.main", fontWeight: 600 }}>
              {fmt(Math.max(0, spAvailable - total))} remaining after
            </Typography>
          </Box>
        </>
      )}

      {hasInsufficientFunds && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 1.5, py: 0.5 }} icon={<WarningIcon fontSize="small" />}>
          <Typography variant="caption" fontWeight={700} display="block">Insufficient spending power</Typography>
          <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
            Need {fmt(total - spAvailable)} more. Contact your account manager to increase your limit.
          </Typography>
        </Alert>
      )}

      {!hasInsufficientFunds && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
          <Typography variant="caption" color="success.main" fontWeight={600} sx={{ fontSize: "0.72rem" }}>
            Sufficient spending power · No fee charged to you
          </Typography>
        </Box>
      )}
    </Paper>
  ) : null;

  // ── Left panel JSX (inlined) ─────────────────────────────────────────── //
  const leftPanelJsx = (
    <Box sx={{ p: { xs: 2.5, md: 4 } }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, borderRadius: 2, bgcolor: Y, mb: 3 }}>
        <BoltIcon sx={{ fontSize: 14, color: Y_INK }} />
        <Typography variant="caption" sx={{ fontWeight: 900, color: Y_INK, fontSize: "0.72rem", letterSpacing: "0.06em" }}>BLITZ SPLIT</Typography>
      </Box>

      {/* Invoice summary */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, borderColor: Y_BORDER, bgcolor: Y_TINT, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: Y_DEEP, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.62rem" }}>Invoice total</Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, fontVariantNumeric: "tabular-nums", color: Y_INK, lineHeight: 1.05, mt: 0.25 }}>{fmt(total)}</Typography>
        </Box>
        <Divider sx={{ borderColor: Y_BORDER, mb: 2 }} />
        <Stack spacing={1.5}>
          {[
            { icon: <BusinessIcon sx={{ fontSize: 15 }} />, label: "From",           value: senderName },
            { icon: <ReceiptIcon  sx={{ fontSize: 15 }} />, label: "PO / Reference", value: poNumber },
            { icon: <CalendarIcon sx={{ fontSize: 15 }} />, label: "Payment terms",  value: termLabel(paymentTerm) },
            {
              icon: <PersonIcon sx={{ fontSize: 15 }} />, label: "Recipients",
              value: rosterLoading ? "Loading…"
                : creatorCount > 0 ? `${creatorCount} contractor${creatorCount !== 1 ? "s" : ""}${preCount > 0 ? ` · ${preCount} ⚡ pre-accepted` : ""}`
                : inv.custom_details?.recipient_count ? `${inv.custom_details.recipient_count} contractors` : "Roster attached",
            },
          ].map(({ icon, label, value }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box sx={{ color: Y_DEEP, mt: "2px", flexShrink: 0 }}>{icon}</Box>
              <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 110, flexShrink: 0, fontSize: "0.82rem" }}>{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>{value}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Spending power panel */}
      {spendingPanelJsx}

      {/* Contractor roster */}
      {showRoster && (
        <Box>
          <Box onClick={() => setRosterOpen((v) => !v)}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", py: 1, px: 0.5, borderRadius: 1.5, "&:hover": { bgcolor: "action.hover" }, userSelect: "none" }}>
            <Typography variant="overline" sx={{ fontWeight: 800, fontSize: "0.62rem", color: "text.secondary", letterSpacing: "0.1em" }}>
              Contractor roster{preCount > 0 && !rosterOpen ? ` · ${preCount} ⚡` : ""}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: Y_DEEP, fontWeight: 700, fontSize: "0.7rem" }}>
                {rosterOpen ? "Collapse" : `Show all ${creatorCount}`}
              </Typography>
              {rosterOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: Y_DEEP }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: Y_DEEP }} />}
            </Box>
          </Box>
          <Collapse in={rosterOpen}>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {payments.map((p, i) => {
                const name = p.recipient_name || p.creator_name || p.creator_id || p.recipient_email || p.name || "Contractor";
                const pre  = p.preapproved || p.blitzpay_preapproved || p.blitzpay;
                return (
                  <Box key={p.id || i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, px: 1.5, borderRadius: 2, bgcolor: pre ? alpha(Y, 0.06) : "transparent", border: "1px solid", borderColor: pre ? Y_BORDER : "transparent" }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: "0.72rem", fontWeight: 800, bgcolor: pre ? Y : "grey.200", color: pre ? Y_INK : "text.secondary", flexShrink: 0 }}>
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{name}</Typography>
                      {p.recipient_email && p.recipient_email !== name && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }} noWrap>{p.recipient_email}</Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {pre && <Chip icon={<BoltIcon sx={{ fontSize: "0.7rem !important" }} />} label="⚡ BlitzPay" size="small" sx={{ height: 20, fontSize: "0.62rem", fontWeight: 800, bgcolor: Y, color: Y_INK, "& .MuiChip-icon": { color: Y_INK } }} />}
                      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", minWidth: 64, textAlign: "right" }}>{fmt(parseFloat(p.amount || 0))}</Typography>
                    </Stack>
                  </Box>
                );
              })}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.25, mt: 0.5, borderTop: "1px solid", borderColor: "divider", px: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>Contractor total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
                  {fmt(payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
                </Typography>
              </Box>
            </Stack>
          </Collapse>
        </Box>
      )}

      {payments.length > 0 && hasZeroAmount && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "divider", mt: 1 }}>
          <Typography variant="overline" sx={{ fontSize: "0.62rem", fontWeight: 800, color: "text.secondary", letterSpacing: "0.1em" }}>Invoice Partner</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
            <Avatar sx={{ bgcolor: Y, color: Y_INK, fontWeight: 800 }}>{senderName.charAt(0).toUpperCase()}</Avatar>
            <Box>
              <Typography variant="body1" fontWeight={800}>{senderName}</Typography>
              <Typography variant="caption" color="text.secondary">{fmt(total)} · {termLabel(paymentTerm)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {rosterLoading && payments.length === 0 && (
        <Box sx={{ py: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={13} sx={{ color: Y_DEEP }} />
          <Typography variant="caption" color="text.secondary">Loading roster…</Typography>
        </Box>
      )}

      {hasExistingNoa && noaData && (
        <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 3, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>✓ NOA on File</Typography>
          {noaData.noa_sent_to && <Typography variant="caption" display="block">Sent to: {noaData.noa_sent_to}</Typography>}
        </Alert>
      )}
    </Box>
  );

  // ── Approval form JSX (inlined) ──────────────────────────────────────── //
  const approvalFormJsx = (
    <>
      {/* Insufficient funds hard block */}
      {hasInsufficientFunds && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>Cannot approve — insufficient spending power</Typography>
          <Typography variant="caption">
            Your available BlitzPay spending power ({fmt(spAvailable)}) is less than this invoice ({fmt(total)}).
            Contact your account manager to increase your limit before approving.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>2. Confirm Authorization</Typography>
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2">Type <strong>"Approve"</strong> to confirm authorization of {fmt(total)}</Typography>
        </Alert>
        <TextField
          label="Type 'Approve' to confirm"
          value={approveWord}
          onChange={(e) => setApproveWord(e.target.value)}
          fullWidth size="small"
          error={approveWord !== "" && approveWord !== "Approve"}
          helperText={approveWord === "Approve" ? "✓ Confirmed" : approveWord ? 'Must type exactly "Approve"' : "Required"}
        />
      </Paper>

      <Paper sx={{ p: 0, mb: { xs: 2, md: 3 }, borderRadius: 2, overflow: "hidden" }}>
        <Box onClick={() => setApSectionOpen((v) => !v)}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: { xs: 2, md: 3 }, cursor: "pointer", bgcolor: "grey.50", "&:hover": { bgcolor: "grey.100" } }}>
          <Typography variant="h6" fontWeight={700}>3. AP Information</Typography>
          {apSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={apSectionOpen}>
          <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 1 }}>
            {hasExistingNoa && (
              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>✓ NOA on File</Typography>
                {noaData?.noa_sent_to && <Typography variant="caption">Sent to: {noaData.noa_sent_to}</Typography>}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField required label="AP Department Email" value={apEmail} onChange={(e) => setApEmail(e.target.value)} fullWidth size="small"
                  error={apEmail !== "" && !emailRegex.test(apEmail)}
                  helperText={apEmail && !emailRegex.test(apEmail) ? "Invalid email" : hasExistingNoa ? "NOA already sent here" : "NOA will be sent here"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required label="Blitz PO / Invoice Number" value={poRef} onChange={(e) => setPoRef(e.target.value)} fullWidth size="small" helperText="Blitz invoice reference" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="ERP System" value={erp} onChange={(e) => setErp(e.target.value)} fullWidth size="small" placeholder="Workday, SAP, Oracle…" helperText="Optional" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label={erp ? `${erp} PO Number` : "External PO Number"} value={externalPo} onChange={(e) => setExternalPo(e.target.value)} fullWidth size="small" helperText="Optional — for reconciliation" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Authority Type</Typography>
                <RadioGroup row value={authorityType} onChange={(e) => setAuthorityType(e.target.value)}>
                  <FormControlLabel value="leader"   control={<Radio size="small" />} label={<Typography variant="body2">Team Leader</Typography>} />
                  <FormControlLabel value="delegate" control={<Radio size="small" />} label={<Typography variant="body2">Delegated Approver</Typography>} />
                  <FormControlLabel value="finance"  control={<Radio size="small" />} label={<Typography variant="body2">Finance Department</Typography>} />
                </RadioGroup>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      <Paper sx={{ p: 0, mb: { xs: 2, md: 3 }, borderRadius: 2, overflow: "hidden" }}>
        <Box onClick={() => setJustifyOpen((v) => !v)}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: { xs: 2, md: 3 }, cursor: "pointer", bgcolor: "grey.50", "&:hover": { bgcolor: "grey.100" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>4. Documentation</Typography>
            <Typography variant="caption" color="text.secondary">(Optional)</Typography>
          </Box>
          {justifyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={justifyOpen}>
          <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 1 }}>
            <TextField multiline rows={3} value={reasoning} onChange={(e) => setReasoning(e.target.value)} fullWidth size="small" placeholder="Business reason…" helperText="Adds to audit trail" sx={{ mb: 2 }} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} gutterBottom>Supporting Documentation</Typography>
            <RadioGroup row value={proofType} onChange={(e) => setProofType(e.target.value)} sx={{ mb: 2 }}>
              <FormControlLabel value="upload"    control={<Radio size="small" />} label="Upload Document" />
              <FormControlLabel value="signature" control={<Radio size="small" />} label="Digital Signature" />
            </RadioGroup>
            {proofType === "upload" && (
              <Box sx={{ textAlign: "center", p: 3, border: "2px dashed", borderColor: "grey.300", borderRadius: 2, bgcolor: "grey.50" }}>
                {!file ? (
                  <>
                    <UploadIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Email approval, signed doc, or evidence · Max 5MB</Typography>
                    <Button variant="contained" component="label" size="small">
                      Choose File
                      <input ref={fileInputRef} type="file" hidden accept="image/*,application/pdf" onChange={handleFileChange} />
                    </Button>
                  </>
                ) : (
                  <Box>
                    {filePreview && <img src={filePreview} alt="preview" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 8, marginBottom: 8 }} />}
                    {!filePreview && <Box sx={{ p: 2, bgcolor: "grey.200", borderRadius: 2, mb: 1 }}><Typography>📄 {file.name}</Typography></Box>}
                    <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(1)} KB</Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <Button variant="outlined" color="error" size="small" onClick={() => { setFile(null); setFilePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>Remove</Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {proofType === "signature" && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Draw your signature</Typography>
                <Box sx={{ border: "2px solid", borderColor: "grey.300", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
                  <SignatureCanvas ref={sigCanvasRef} penColor="black"
                    canvasProps={{ width: 600, height: 150, style: { width: "100%", height: "150px", cursor: "crosshair", touchAction: "none" } }}
                  />
                </Box>
                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => sigCanvasRef.current?.clear()}>Clear</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={700} gutterBottom>Legal Acknowledgment Required</Typography>
        <FormControlLabel
          control={<Checkbox checked={agreeAuthority} onChange={(e) => setAgreeAuthority(e.target.checked)} size="small" />}
          label={<Typography variant="body2"><strong>APPROVAL AUTHORITY:</strong> I certify I have delegated authority to approve invoices of this type and amount.</Typography>}
          sx={{ mb: 1, alignItems: "flex-start" }}
        />
        <Divider sx={{ my: 1 }} />
        <FormControlLabel
          control={<Checkbox checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} size="small" />}
          label={
            <Typography variant="body2">
              <strong>RECEIVABLE ASSIGNMENT:</strong> Payment of {fmt(total)} must be remitted directly to Blitz Systems Corp per the {hasExistingNoa ? "NOA on file" : "Notice of Assignment (NOA)"}.{" "}
              <strong>No additional fee is charged to your organization</strong> — the Blitz discount rate is borne by the invoicing party.
            </Typography>
          }
          sx={{ alignItems: "flex-start" }}
        />
      </Alert>
    </>
  );

  const submitLabel = isLoading ? "Processing…" : action === "approve" ? `Approve Split · ${fmt(total)}` : "Decline Invoice";
  const submitDisabled = (action === "approve" && !isApproveValid) || isLoading || alreadyProcessed;

  const footerJsx = (stack = false) => (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, bgcolor: "background.paper", px: stack ? 2.5 : 4, pb: stack ? 3 : 3 }}>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>{error}</Alert>}
      {alreadyProcessed && <Alert severity="info" sx={{ mb: 1.5 }}>This invoice has already been {invoiceStatus}.</Alert>}
      {hasInsufficientFunds && action === "approve" && (
        <Alert severity="error" sx={{ mb: 1.5 }}>Approval blocked — insufficient BlitzPay spending power.</Alert>
      )}
      {stack ? (
        <Stack spacing={1}>
          <Button fullWidth variant="contained" size="large"
            sx={{ fontWeight: 800, height: 52, borderRadius: 2, textTransform: "none", bgcolor: action === "approve" ? Y : undefined, color: action === "approve" ? Y_INK : undefined, "&:hover": { bgcolor: action === "approve" ? Y_DARK : undefined }, "&.Mui-disabled": { bgcolor: action === "approve" ? alpha(Y, 0.3) : undefined, color: action === "approve" ? alpha(Y_INK, 0.4) : undefined } }}
            color={action === "approve" ? undefined : "error"}
            disabled={submitDisabled}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <BoltIcon />}
            onClick={handleSubmit}
          >{submitLabel}</Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleClose} disabled={isLoading} sx={{ fontWeight: 600, height: 48, borderRadius: 2, textTransform: "none" }}>Cancel</Button>
        </Stack>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" color="error" size="large" startIcon={<CancelIcon />}
              disabled={isLoading || alreadyProcessed || action !== "decline"} onClick={handleSubmit}
              sx={{ px: 3, borderRadius: 2, fontWeight: 700, textTransform: "none" }}>Decline</Button>
            <Button variant="contained" size="large"
              startIcon={isLoading ? <CircularProgress size={18} sx={{ color: Y_INK }} /> : <BoltIcon />}
              disabled={submitDisabled} onClick={handleSubmit}
              sx={{ flex: 1, fontWeight: 800, fontSize: "1rem", textTransform: "none", bgcolor: Y, color: Y_INK, borderRadius: 2, boxShadow: `0 4px 18px ${alpha(Y, 0.4)}`, "&:hover": { bgcolor: Y_DARK }, "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) } }}
            >{isLoading ? "Processing…" : `Approve Split · ${fmt(total)}`}</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.5 }}>
            You authorize payment of {fmt(total)} with no additional fee to your organization.
          </Typography>
        </>
      )}
    </Box>
  );

  // ── Mobile ───────────────────────────────────────────────────────────── //
  if (isMobile) {
    return (
      <Box sx={{ position: "fixed", inset: 0, bgcolor: "background.default", zIndex: theme.zIndex.modal, display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: Y, color: Y_INK }}><GavelIcon fontSize="small" /></Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Approval Required</Typography>
                <Typography variant="caption" color="text.secondary">Blitz Split · {senderName}</Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", pb: "160px" }}>
          {leftPanelJsx}
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Alert severity="error" icon={<SecurityIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.82rem" }}>⚠️ Immediate Financial Impact</Typography>
              <Typography variant="caption">{action === "approve" ? `Approving commits ${fmt(total)}. Cannot be undone.` : "Declining permanently cancels this invoice."}</Typography>
            </Alert>
            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>1. Select Action</Typography>
              <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
                <FormControlLabel value="approve" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="success.main">Approve Split ⚡</Typography><Typography variant="caption" color="text.secondary">Authorize payment · no fee to you</Typography></Box>}
                  sx={{ mb: 1, p: 1.5, border: action === "approve" ? "2px solid" : "1px solid", borderColor: action === "approve" ? "success.main" : "grey.300", borderRadius: 2, mr: 0 }}
                />
                <FormControlLabel value="decline" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="error.main">Decline Invoice</Typography><Typography variant="caption" color="text.secondary">Cancel permanently</Typography></Box>}
                  sx={{ p: 1.5, border: action === "decline" ? "2px solid" : "1px solid", borderColor: action === "decline" ? "error.main" : "grey.300", borderRadius: 2, mr: 0 }}
                />
              </RadioGroup>
            </Paper>
            {action === "approve" && approvalFormJsx}
            {action === "decline" && (
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Decline Reason (Optional)</Typography>
                <TextField multiline rows={3} value={reasoning} onChange={(e) => setReasoning(e.target.value)} fullWidth size="small" placeholder="Reason for declining…" />
              </Paper>
            )}
          </Box>
        </Box>

        <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, boxShadow: "0 -2px 10px rgba(0,0,0,0.06)" }}>
          {footerJsx(true)}
        </Box>
      </Box>
    );
  }

  // ── Desktop ──────────────────────────────────────────────────────────── //
  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "background.default", zIndex: theme.zIndex.modal, display: "flex", flexDirection: "column" }}>
      <Box sx={{ height: 72, px: 4, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: Y, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 14px ${alpha(Y, 0.5)}` }}>
            <GavelIcon sx={{ color: Y_INK, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Invoice Approval Required</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip icon={<BoltIcon sx={{ fontSize: "13px !important" }} />} label="Blitz Split" size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, bgcolor: Y, color: Y_INK, "& .MuiChip-icon": { color: Y_INK } }} />
              {hasExistingNoa && <Chip icon={<CheckIcon />} label="NOA on File" color="success" size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />}
              {hasInsufficientFunds && <Chip icon={<WarningIcon />} label="Insufficient Funds" color="error" size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ bgcolor: "action.hover", borderRadius: 2 }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${Y} 0%, ${alpha(Y, 0.35)} 100%)`, flexShrink: 0 }} />

      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <Box sx={{ width: 380, flexShrink: 0, borderRight: "1px solid", borderColor: "divider", overflowY: "auto", bgcolor: "background.paper" }}>
          {leftPanelJsx}
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 4, flex: 1 }}>
            <Alert severity="error" icon={<SecurityIcon />} sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>⚠️ Immediate Financial Impact</Typography>
              <Typography variant="caption">{action === "approve" ? `Approving commits ${fmt(total)} against your BlitzPay spending power. Cannot be undone.` : "Declining permanently cancels this invoice."}</Typography>
            </Alert>
            <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>1. Select Action</Typography>
              <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
                <FormControlLabel value="approve" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="success.main">Approve Split ⚡</Typography><Typography variant="caption" color="text.secondary">Authorize payment to all contractors · no fee charged to your organization</Typography></Box>}
                  sx={{ mb: 1.5, p: 2, border: action === "approve" ? "2px solid" : "1px solid", borderColor: action === "approve" ? "success.main" : "grey.300", borderRadius: 2, mr: 0 }}
                />
                <FormControlLabel value="decline" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="error.main">Decline Invoice</Typography><Typography variant="caption" color="text.secondary">Permanently cancel</Typography></Box>}
                  sx={{ p: 2, border: action === "decline" ? "2px solid" : "1px solid", borderColor: action === "decline" ? "error.main" : "grey.300", borderRadius: 2, mr: 0 }}
                />
              </RadioGroup>
            </Paper>
            {action === "approve" && approvalFormJsx}
            {action === "decline" && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Decline Reason (Optional)</Typography>
                <TextField multiline rows={4} value={reasoning} onChange={(e) => setReasoning(e.target.value)} fullWidth placeholder="Optionally provide a reason for declining…" />
              </Paper>
            )}
          </Box>
          {footerJsx(false)}
        </Box>
      </Box>
    </Box>
  );
};

export default BBCSplitApprovalPage;